/** Administration as hg-order gives it: the markets, their booking windows, the search's tuning, v1's log. */
import type { components as Audit } from "@/shared/api/audit.schema";
import type { components as Auth } from "@/shared/api/auth.schema";
import type { components as Billing } from "@/shared/api/billing.schema";
import type { components as Notify } from "@/shared/api/notify.schema";
import type { components } from "@/shared/api/order.schema";

type Schemas = components["schemas"];

/** A partner whose app holds a mini-app of ours, or Telegram sign-in: switched on or off in hg-auth. */
export type Partner = Auth["schemas"]["PartnerResponse"];

/** One event told, or to be told, to one partner's server: hg-notify's. */
export type Delivery = Notify["schemas"]["PartnerDeliveryResponse"];
export type DeliveryState = Notify["schemas"]["PartnerDeliveryState"];

/** Where telling a partner of an event stands, in the order a delivery lives through them. */
export const DELIVERY_STATES = [
  "pending",
  "delivered",
  "dead",
  "skipped",
] as const satisfies readonly DeliveryState[];

/** The partners orders are made inside the apps of: any other code is shown as hg-notify names it. */
export const DELIVERY_PARTNERS = ["click", "sino"] as const;

/** What hg-notify tells a partner of, where the app has words for it. */
export const DELIVERY_KINDS = [
  "visit.assigned",
  "visit.en_route",
  "visit.on_site",
  "visit.completed",
  "visit.cancelled",
  "order.action_required",
] as const;

export type Market = Schemas["AdminMarketResponse"];
export type MarketInput = Schemas["SaveMarketRequest"];
export type VisitWindow = Schemas["VisitWindowResponse"];
export type WindowInput = Schemas["WindowInput"];

/** The numbers a search runs by: a market's, an area's, a profession's, or the defaults. */
export type Tuning = Schemas["TuningBody"];
/** A row staff set: a market's, or an area's, a profession's or both within it. */
export type DispatchRow = Schemas["DispatchSettingsResponse"];
export type DispatchInput = Schemas["PutDispatchSettingsRequest"];
export type DispatchList = Schemas["DispatchSettingsListResponse"];
/** What a market without a row of its own runs on, and who set it last. */
export type DispatchDefaults = Schemas["DispatchDefaultsResponse"];
export type DefaultsInput = Schemas["PutDispatchDefaultsRequest"];

/** A market's settings for courses and specialist-made visits, and who set them last. */
export type CourseSettings = Schemas["CourseSettingsResponse"];
/** The settings as hg-order takes them: the whole row. */
export type CourseTerms = Schemas["CourseTermsRequest"];
/** What candidate settings come to for one service in one area: three visits. */
export type CourseExample = Schemas["CourseExampleResponse"];
export type CourseExampleVisit = Schemas["CourseExampleVisit"];
/** A service of the catalog, as the example's picker lists them. */
export type CatalogService = Schemas["ServiceResponse"];

/** The rules of the course bonus in a market: hg-billing's. */
export type CourseBonus = Billing["schemas"]["CourseBonusSettingResponse"];
export type CourseBonusInput = Billing["schemas"]["SetCourseBonusSettingRequest"];
export type BonusKind = Billing["schemas"]["BonusKind"];
/** How the bonus is counted: the last visit's commission given back, or the same sum for every course. */
export const BONUS_KINDS = [
  "last_visit_commission",
  "fixed",
] as const satisfies readonly BonusKind[];

/** After how long a visit somebody has counts as stuck, and who set it last. */
export type StuckSettings = Schemas["StuckVisitSettingsResponse"];
export type StuckInput = Schemas["PutStuckVisitSettingsRequest"];

export type AuditRow = Schemas["V1AuditRowResponse"];
/** What a change kept of the values, field by field: any JSON v1 wrote. */
export type AuditValues = Schemas["V1AuditValues"];
export type AuditKind = Schemas["V1AuditKind"];

/** One thing a staff member did in v2, as hg-audit keeps it: who, to what, what changed. */
export type StaffAction = Audit["schemas"]["ActionResponse"];
export type StaffActionChange = Audit["schemas"]["ActionChangeResponse"];
/** A kind of action the trail holds, with how many of it there are. */
export type StaffActionKind = Audit["schemas"]["ActionKindResponse"];
